import React, { useState, useEffect, useCallback, useContext } from "react";

import { getMeetingRequests } from "api/getMeetingRequests";
import { respondToMeetingRequest } from "api/respondToMeetingRequest";
import { useAccountStatus } from "hooks/useAccountStatus";
import MeetingSummary from "components/MeetingSummary";
import FieldText from "components/FieldText";
import { formatMeetingDetailsForAttendees } from "services/formatMeetingDetailsForAttendees";
import { isValidGoogleMeetLink } from "services/isValidGoogleMeetLink";
import ModalRespond from "components/ModalRespond/ModalRespond";
import RequestsSection from "components/PageDashboard/RequestsSection";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import terminology from "terminology.json";

const cols = [
	{ key: "startDate", heading: "Date" },
	{ key: "startTime", heading: "From" },
	{ key: "endTime", heading: "Until" },
	{ key: "timezone", heading: "Timezone" },
	{ key: "partner", heading: "With" },
	{ key: "medium", heading: "Using" },
	{ key: "message", heading: "Message" }
];

const MeetingRequestsSection: React.FunctionComponent = () => {
	const {
		_populated: accountStatusPopulated,
		defaultGoogleMeetLink
	} = useAccountStatus();

	const [googleMeetLink, setGoogleMeetLink] = useState<string | undefined>(
		undefined
	);

	useEffect(() => {
		if (!googleMeetLink && defaultGoogleMeetLink) {
			setGoogleMeetLink(defaultGoogleMeetLink);
		}
	}, [googleMeetLink, setGoogleMeetLink, defaultGoogleMeetLink]);

	const [{ uid }] = useContext(AuthContext);
	const fetchRequests = useCallback<typeof getMeetingRequests>(
		arg => {
			return getMeetingRequests(
				{ ...arg, earliestStartDateUTC: new Date() },
				{ uid }
			);
		},
		[uid]
	);

	if (!accountStatusPopulated) {
		return null;
	}

	return (
		<RequestsSection
			fetchRequests={fetchRequests}
			queryStringKey="meetingrequest"
			dashboardContextKey="meetingsSectionRefreshNum"
			respondToItem={respondToMeetingRequest}
			cols={cols}
			formatItemDetails={formatMeetingDetailsForAttendees}
			itemsDescription={terminology.meetingRequests}
			responseModal={({
				currentResponseApiRequest,
				currentResponseNote,
				setCurrentResponse,
				setCurrentResponseNote,
				setRespondingTo,
				respondingTo,
				currentResponse,
				sendResponse,
				setCurrentResponseApiRequestDispatchTime
			}) => (
				<ModalRespond
					isOpen={!!respondingTo}
					onCancel={() => {
						setCurrentResponse(undefined);
						setCurrentResponseNote(undefined);
						setRespondingTo(undefined);
					}}
					OKButtonIsDisabled={
						respondingTo &&
						currentResponse === true &&
						respondingTo.medium === "google-meet" &&
						(!googleMeetLink || !isValidGoogleMeetLink(googleMeetLink))
					}
					currentResponse={currentResponse}
					setCurrentResponse={setCurrentResponse}
					sendResponse={sendResponse}
					respondingTo={respondingTo}
					responseApiRequest={currentResponseApiRequest}
					responseNote={currentResponseNote}
					setResponseNote={setCurrentResponseNote}
					additionalValidation={respondingTo => {
						if (
							respondingTo.medium === "google-meet" &&
							currentResponse === true
						) {
							if (!googleMeetLink) {
								throw new Error("No Google Meet Link");
							}
							if (!isValidGoogleMeetLink(googleMeetLink)) {
								throw new Error("Invalid Google Meet Link");
							}
						}
					}}
					additionalDataToSubmit={(respondingTo, currentResponse: boolean) => {
						if (
							respondingTo.medium === "google-meet" &&
							currentResponse === true
						) {
							return {
								googleMeetLink
							};
						} else {
							return {};
						}
					}}
					onResponseSent={setCurrentResponseApiRequestDispatchTime}
					summary={(respondingTo, uid) => (
						<MeetingSummary event={respondingTo} currentUid={uid} />
					)}
					heading="Can you make this?"
					extraContent={
						respondingTo && respondingTo.medium === "google-meet" ? (
							<div className="col-12">
								<h3>Google Meet Link</h3>
								<p>
									Because this is being held over Google Meet, if you accept the
									invitation, you need to{" "}
									<a
										href="https://meet.google.com"
										target="_blank"
										rel="noopener noreferrer"
									>
										go to Google Meet in a new tab
									</a>
									, generate a meeting link ('new meeting &gt; create a meeting
									for later'), and copy/paste it back here.
								</p>

								<FieldText
									value={googleMeetLink ? googleMeetLink : ""}
									onChange={e => setGoogleMeetLink(e.currentTarget.value)}
									placeholder="https://meet.google.com..."
									highlightValidationError={
										!!googleMeetLink && !isValidGoogleMeetLink(googleMeetLink)
									}
								/>
							</div>
						) : null
					}
				/>
			)}
		/>
	);
};

export default MeetingRequestsSection;
